/**
 * LoR List
 *
 * Vertical card list of generated LoR documents with coverage info and actions:
 * load, view, and download. Scrollable for longer lists. All network-side
 * effects are handled by the parent via callbacks.
 */
import { format } from 'date-fns';
import { Download, Eye, FileText } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { CoverageSchema } from '@/services/types/coverage-types';

import type { MatterDocLite } from '../types';

type Props = {
  matterId: string;
  docs: MatterDocLite[];
  isLoading: boolean;
  docsError?: unknown;
  templatesError?: unknown;
  backendHost: string;
  /** Map of coverage_id -> coverage for display */
  coveragesMap?: Map<string, CoverageSchema>;
  /** Current coverage ID to filter docs */
  currentCoverageId?: string;
  /** Currently selected/loaded doc ID for highlighting */
  selectedDocId?: string | null;
  onView: (id: string) => void;
  onLoad: (id: string) => void;
};

export const LorList: React.FC<Props> = ({
  matterId,
  docs,
  isLoading,
  docsError,
  templatesError,
  backendHost,
  coveragesMap,
  currentCoverageId,
  selectedDocId,
  onView,
  onLoad,
}) => {
  // Filter docs to show only those for current coverage or unknown coverage
  const filteredDocs = React.useMemo(() => {
    if (!currentCoverageId) return docs;
    return docs.filter(
      (doc) => !doc.coverage_id || doc.coverage_id === currentCoverageId,
    );
  }, [docs, currentCoverageId]);
  const getCategoryAbbrev = (category: string | null | undefined): string => {
    if (!category) return '';
    switch (category) {
      case 'FIRST_PARTY':
        return '1P';
      case 'FIRST_PARTY_HEALTH':
        return '1P Health';
      case 'THIRD_PARTY':
        return '3P';
      case 'NOT_LIABLE_INVOLVED_PARTY':
        return 'NL';
      case 'UNKNOWN':
        return '?';
      default:
        return category;
    }
  };

  const getCoverageName = (
    coverageId: string | null | undefined,
    doc: MatterDocLite,
  ): string => {
    // Try to get carrier name from doc's field_values as fallback
    const docCarrierName =
      (doc.field_values?.carrier_name as string | undefined) ??
      (doc.merge_fields?.carrier_name as string | undefined);

    if (!coverageId) {
      return docCarrierName || 'Unknown Coverage';
    }

    const coverage = coveragesMap?.get(coverageId);
    if (!coverage) {
      return docCarrierName || 'Unknown Coverage';
    }

    const carrierName =
      coverage.carrier?.name || docCarrierName || 'Unknown Carrier';
    const abbrev = getCategoryAbbrev(coverage.category);
    return abbrev ? `${carrierName} (${abbrev})` : carrierName;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">
          Generated Letters of Representation
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="text-sm text-gray-500">Loading documents…</div>
        )}
        {Boolean(templatesError) && (
          <div className="text-sm text-red-600">Failed to load templates.</div>
        )}
        {Boolean(docsError) && (
          <div className="text-sm text-red-600">Failed to load documents.</div>
        )}
        {!isLoading && filteredDocs.length === 0 && (
          <div className="text-sm text-gray-500">No LoRs generated yet.</div>
        )}
        {filteredDocs.length > 0 && (
          <div className="max-h-[400px] overflow-y-auto space-y-2 pr-1">
            {filteredDocs.map((doc, index) => {
              const isLatest = index === 0;
              const isSelected = doc.id === selectedDocId;

              return (
                <div
                  key={doc.id}
                  className={cn(
                    'rounded-lg border p-3 transition-colors',
                    isSelected
                      ? 'border-violet-300 bg-violet-50 ring-1 ring-violet-200'
                      : isLatest
                        ? 'border-violet-200 bg-violet-50/40'
                        : 'border-zinc-200 bg-white',
                  )}
                >
                  {/* Row 1: Template name + badges */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <FileText className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-zinc-900">
                      {doc.document_template?.name ||
                        'Letter of Representation'}
                    </span>
                    {isSelected && (
                      <span className="rounded-full bg-violet-200 px-1.5 py-0.5 text-[10px] font-semibold text-violet-800">
                        Loaded
                      </span>
                    )}
                    {isLatest && !isSelected && (
                      <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold text-violet-700">
                        Latest
                      </span>
                    )}
                    {doc.uploaded_artifact_id && (
                      <>
                        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                          Uploaded File
                        </span>
                        {doc.original_filename && (
                          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 uppercase">
                            {doc.original_filename
                              .split('.')
                              .pop()
                              ?.toLowerCase() || 'file'}
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Row 2: Coverage + date + filename */}
                  <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500 flex-wrap">
                    <span>{getCoverageName(doc.coverage_id, doc)}</span>
                    <span className="text-zinc-300">·</span>
                    <span>
                      {format(new Date(doc.created_at), 'MMM d, yyyy h:mm a')}
                    </span>
                    {doc.original_filename && (
                      <>
                        <span className="text-zinc-300">·</span>
                        <span className="truncate max-w-[200px]">
                          {doc.original_filename}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Row 3: Actions */}
                  <div className="mt-2 flex items-center gap-1.5">
                    <Button
                      size="sm"
                      variant={isSelected ? 'secondary' : 'outline'}
                      className="h-7 text-xs px-2.5"
                      onClick={() => onLoad(doc.id)}
                      disabled={isSelected}
                    >
                      {isSelected ? 'Loaded' : 'Load'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs px-2 gap-1"
                      onClick={() => onView(doc.id)}
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs px-2 gap-1"
                      onClick={() => {
                        const url = `${backendHost}/api/matters/${matterId}/documents/${doc.id}/download/`;
                        window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <Download className="h-3 w-3" />
                      Download
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
